import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { useTrackMutation } from '@mote/api'
import { TRACK_EVENT_NAMES } from '@mote/helpers'
import {
  PermissionDenied,
  getAudioPermissions
} from './getUserMerdiaPermissions'

export interface MicrophonePermissionContextValue {
  ask: () => Promise<boolean>
  reset: () => void
  state: MicrophonePermissionState
}

export const MicrophonePermissionContext =
  createContext<MicrophonePermissionContextValue | null>(null)

interface MicrophonePermissionProviderProps {
  children: ReactNode
  askOnRender?: boolean
}

export type MicrophonePermissionState =
  | {
      status: 'success'
    }
  | {
      status: 'idle'
    }
  | {
      status: 'error'
      message: string
      title: string
    }

export function MicrophonePermissionProvider(
  props: MicrophonePermissionProviderProps
): JSX.Element {
  const trackMutation = useTrackMutation()
  const [microphonePermissionState, setMicrophonePermissionState] =
    useState<MicrophonePermissionState>({ status: 'idle' })

  useEffect(() => {
    if (props.askOnRender) {
      ask()
    }
  }, [props.askOnRender])

  async function ask(): Promise<boolean> {
    if (microphonePermissionState.status === 'success') {
      return true
    }

    try {
      //@ts-expect-error
      trackMutation.mutate({
        variables: {
          event: TRACK_EVENT_NAMES.permission.microphone.requestTriggered
        }
      })
      await getAudioPermissions()
      setMicrophonePermissionState({ status: 'success' })
      //@ts-expect-error
      trackMutation.mutate({
        variables: {
          event: TRACK_EVENT_NAMES.permission.microphone.requestEnabled
        }
      })
      return true
    } catch (error) {
      //@ts-expect-error
      trackMutation.mutate({
        variables: {
          event: TRACK_EVENT_NAMES.permission.microphone.requestBlocked
        }
      })
      if (error instanceof PermissionDenied) {
        setMicrophonePermissionState({
          status: 'error',
          message: error.message,
          title: 'Your microphone is blocked'
        })
      }
      return false
    }
  }

  return (
    <MicrophonePermissionContext.Provider
      value={{
        ask,
        state: microphonePermissionState,
        reset: () => setMicrophonePermissionState({ status: 'idle' })
      }}
    >
      {props.children}
    </MicrophonePermissionContext.Provider>
  )
}

export function useMicrophonePermissionContext() {
  const value = useContext(MicrophonePermissionContext)
  if (!value) {
    throw new Error('Missing MicrophonePermissionContext')
  }
  return value
}
